/** Number formats for the Overview and Performance pages. */

/** "1,204" */
export function count(n: number): string {
  return n.toLocaleString("en-US");
}

/** A 0..1 rate as "2.5%"; "0%" and "100%" stay whole. */
export function rate(r: number): string {
  if (!Number.isFinite(r) || r <= 0) return "0%";
  const pct = r * 100;
  const fixed = pct.toFixed(1);
  return `${fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed}%`;
}

/**
 * First orders per 100 clicks in one row, as a plain number, or "—" when there were no clicks. Not a conversion rate:
 * an order is counted on its own date, not its click's, so a row can pass 100. The KPI tile is the cohort rate.
 */
export function ordersPer100Clicks(
  firstOrders: number,
  clicks: number,
): string {
  if (clicks <= 0) return "—";
  const fixed = ((firstOrders / clicks) * 100).toFixed(1);
  return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
}

/**
 * The previous period starts `2 * days` back. Past the retention the clicks are gone while sign-ups, orders and fees
 * remain, so comparing click figures would read as a drop.
 */
export const comparisonReachesPastRetention = (
  days: number,
  retentionDays: number,
): boolean => days * 2 > retentionDays;

/** The range itself is missing clicks; the ones kept are the ones that converted, so rates on them read too high. */
export const rangeReachesPastRetention = (
  days: number,
  retentionDays: number,
): boolean => days > retentionDays;

/** The one sentence the report shows when its clicks are incomplete, or null when they are all there. */
export function clickRetentionNote(
  days: number,
  retentionDays: number,
): string | null {
  const base = `Click counts cover the last ${retentionDays} days; older clicks are deleted`;
  if (rangeReachesPastRetention(days, retentionDays)) return `${base}.`;
  return comparisonReachesPastRetention(days, retentionDays)
    ? `${base}, so clicks are not compared with the previous period.`
    : null;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * A `YYYY-MM-DD` day or `YYYY-MM` month as its parts. Spelled by hand: ICU's
 * en-GB short month for September is "Sept", which reads oddly next to "Oct".
 */
function parts(bucket: string) {
  const m = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(bucket);
  if (!m) return null;
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return null;
  return {
    year: m[1],
    month,
    short: month.slice(0, 3),
    day: m[3] === undefined ? null : String(Number(m[3])),
  };
}

/** A chart axis label: "7 Oct" for a day, "Oct 2026" for a month (`YYYY-MM`). */
export function bucketLabel(bucket: string): string {
  const p = parts(bucket);
  if (!p) return bucket;
  return p.day === null ? `${p.short} ${p.year}` : `${p.day} ${p.short}`;
}

/** A table row's date: "7 Oct 2026" for a day, "October 2026" for a month. */
export function bucketTitle(bucket: string): string {
  const p = parts(bucket);
  if (!p) return bucket;
  return p.day === null
    ? `${p.month} ${p.year}`
    : `${p.day} ${p.short} ${p.year}`;
}

export { countryName } from "@/lib/format";
